<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" :hide-details="true" />
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <label class="label-input">Layout</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="fields.layout.gap"
                  type="number"
                  label="Gap between image and content"
                  min="0"
                  suffix="px"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="fields.image.position"
                  label="Image position (desktop)"
                  :items="positionOptions"
                  item-text="text"
                  item-value="value"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.backgroundContent"
                  name="backgroundContent"
                  label="Content background"
                  default-color="#006196"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.textColorContent"
                  name="textColorContent"
                  label="Content text color"
                  default-color="#ffffff"
                />
              </v-col>
            </v-row>
            <p class="text-caption text--secondary mt-2 mb-0">On mobile, image is always displayed above content.</p>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Side Image</label>
          <div class="group-item">
            <UploadImage
              @callback="handleUploadSideImage"
              :image="{ imageSrc: fields.image.src }"
              :height="`200`"
              :typeUpload="'image'"
              @callbackDelete="handleRemoveSideImage"
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Content Header</label>
          <ckeditor :editor="editor" v-model="fields.header" :config="editorConfig" />
        </div>

        <div class="mb-4">
          <div class="d-flex justify-space-between align-center">
            <label class="label-input mb-0">Items ({{ fields.items.length }})</label>
            <v-btn small text color="primary" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add item</span>
            </v-btn>
          </div>
          <v-expansion-panels accordion class="mt-2">
            <v-expansion-panel v-for="(item, index) in fields.items" :key="`orthopedic-card-features-item-${index}`">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center">
                  <div class="line-clamp-1" v-html="getItemPreview(item)"></div>
                  <v-btn icon @click.stop="handleRemoveItem(index)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <UploadImage
                  @callback="obj => handleUploadImage(obj, index)"
                  :image="{ imageSrc: item.image }"
                  :height="`100`"
                  :typeUpload="'image'"
                  @callbackDelete="handleRemoveImage(index)"
                />
                <label class="mb-2 d-block mt-3">Content</label>
                <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';
import ColorPicker from './common/color-picker.vue';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';

export default {
  name: 'OrthopedicCardFeaturesSectionEditor',
  components: {
    SectionSettingsContainerFuild,
    ColorPicker,
    UploadImage,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      positionOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Right', value: 'right' },
      ],
      fields: {
        title: 'Orthopedic card features',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        image: {
          src: '',
          position: 'left',
        },
        layout: {
          gap: 8,
        },
        backgroundContent: '#006196',
        textColorContent: '#ffffff',
        header: '',
        items: [],
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        image: { ...this.fields.image, ...(extract.image || {}) },
        layout: { ...this.fields.layout, ...(extract.layout || {}) },
        items: Array.isArray(extract.items) ? extract.items : this.fields.items,
      };
    },
    getItemPreview(item) {
      const text = (item.content || '').replace(/<[^>]*>/g, '').trim();
      return text || 'Item';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionOrthopedicCardFeatures(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleUploadSideImage(obj) {
      this.fields.image.src = (obj && obj.imageSrc) || '';
    },
    handleRemoveSideImage() {
      this.fields.image.src = '';
    },
    handleAddItem() {
      this.fields.items.push({
        image: '',
        content:
          '<h2><span style="font-size:24px;"><strong>Feature title</strong></span></h2><p><span style="font-size:22px;">Feature description</span></p>',
      });
    },
    handleRemoveItem(index) {
      if (this.fields.items.length <= 1) return;
      this.fields.items.splice(index, 1);
    },
    handleUploadImage(obj, index) {
      this.fields.items[index].image = obj.imageSrc;
    },
    handleRemoveImage(index) {
      this.fields.items[index].image = '';
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
